/**
 * BreadcrumbNav - Contextual navigation for detail pages
 * =======================================================
 * Provides consistent "← Page Title" navigation header with optional breadcrumb trail
 * 
 * Format: ← Customer Name (with breadcrumb CRM > Customer Name below on hover/expand)
 * 
 * Usage:
 * <BreadcrumbNav 
 *   items={[
 *     { label: 'CRM', path: '/crm' },
 *     { label: 'Rahul Sharma' }  // Current page (no path = title)
 *   ]}
 * />
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const BreadcrumbNav = ({ 
  items = [],
  fallbackPath = '/crm',
  className = ''
}) => {
  const navigate = useNavigate();
  
  // Get page title (last item) and breadcrumb trail (all items)
  const pageTitle = items.length > 0 ? items[items.length - 1].label : '';
  
  // Back button handler - returns to previous page or fallback
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      const previousItem = items.length > 1 ? items[items.length - 2] : null;
      navigate(previousItem?.path || fallbackPath);
    }
  };
  
  // Handle breadcrumb item click
  const handleItemClick = (item, index) => {
    if (item.path && index < items.length - 1) {
      navigate(item.path);
    }
  };
  
  return (
    <div className={`bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-30 ${className}`}>
      <div className="flex items-center gap-2">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
          data-testid="page-back-btn"
          aria-label="Go back"
        >
          <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
        </button>
        
        {/* Title + Breadcrumb */}
        <div className="min-w-0 flex-1">
          {/* Page Title */}
          <h1 className="text-lg font-bold text-[#111827] truncate">{pageTitle}</h1>
          
          {/* Breadcrumb Trail - shows parent path */}
          {items.length > 1 && (
            <nav className="flex items-center gap-0.5 mt-0.5" aria-label="Breadcrumb">
              <ol className="flex items-center text-xs text-slate-500">
                {items.slice(0, -1).map((item, index) => (
                  <li key={index} className="flex items-center">
                    {index > 0 && (
                      <ChevronRight size={12} className="mx-0.5 text-slate-300" />
                    )}
                    {item.path ? (
                      <button
                        onClick={() => handleItemClick(item, index)}
                        className="hover:text-[#5B3DF5] hover:underline transition-colors"
                        data-testid={`breadcrumb-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <span data-testid={`breadcrumb-item-${index}`}>{item.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreadcrumbNav;
